/**
 * 제품 등록 — 한 폼 /products/new(현민 2026-10-22 · 회의 2026-10-21 §2 · VOCABULARY "제품 등록 — 한 폼").
 * 카테고리(21종 · 섀시가 한 값) · 파트넘버(번호 한 칸 — 종류는 조사가, 2026-10-29) · 제조사 · 디스크립션(셋 필수) · 검색어 · 메모. 타입은 카테고리가 가른다 — CHASSIS 면 서버(구성 없음 · 파트넘버 = SKU), 아니면 파트.
 * 카탈로그는 찾기 칸 + [찾기] → `/catalog?pick=1&q=` 새 탭 → 결과 줄 [선택] → BroadcastChannel 로 폼에 쏙(lib/catalog-pick). 규칙은 product-create/form.ts, 여긴 액션과 그리기.
 * 백엔드 POST /products(파트) · POST /products/server(서버). 옛 두 칸(왼쪽 찾기 · 오른쪽 담기, 2026-10-15 ~ 10-20)은 접음.
 */

import { SolaView, htmlSolaWithInstance } from '@hm-spt/sola';
import { SPEC_CATEGORIES, toSpecCategory } from '@hcltech/shared/server-config/spec-category';
import type { SpecCategory } from '@hcltech/shared/server-config/spec-category';
import * as api from '../api/client';
import { PART_NUMBER_COLUMN_SENTENCE_WORD, TOKEN_ROLE_WORD, pnKeyOf } from '../api/client';
import type { CatalogPick, CatalogPickNone, DescriptionReading } from '../api/client';
import { assert } from '../lib/assert';
import { listenCatalogPick, listenCatalogPickNone, openCatalogPickTab } from '../lib/catalog-pick';
import { escapeHtml } from '../lib/cells';
import { focusedSlotOrNull, refocusSlot, renderSlotTable, slotRoleOf } from '../components/slot-reading';
import { emptyProductForm, formWithField, formWithFoundText, formWithLookup, formWithPick, isCatalogLinked, isChassisForm, isCategoryNeeded, isReadingShown, missingRequiredRolesOf, openRequestBlockOrNull, partInputOf, saveBlockOrNull, serverInputOf, formWithSlotFill, slotFillsOf, formWithVendorChoice, VENDOR_OTHER } from './product-create/form';
import { E_FIND_FILL_FIELDS, E_PRODUCT_FORM_FIELDS } from './product-create/types';
import type { EFindFillField, EProductFormField, FormLookup, ProductFormData } from './product-create/types';

const LOOKUP_DEBOUNCE_MS = 300;     // 파트넘버를 치는 동안 글자마다 서버를 안 부른다
const READING_DEBOUNCE_MS = 300;    // 디스크립션을 치는 동안 글자마다 규칙 읽기를 안 부른다
const CONFLICT_PREFIX = 'HTTP 409';
const PART_NUMBER_KEY: EProductFormField = 'part_number';
const CHASSIS_CATEGORY: SpecCategory = 'CHASSIS';
const POPUP_BLOCKED_WORD = '팝업이 막혔습니다 — [찾기] 를 한 번 더 눌러 주세요';

export class ProductCreateView extends SolaView<ProductFormData> {
  override events = {
    input:   { '@field': 'handleField', '@find-input': 'handleField', '@slot-fill': 'handleSlotFill' },
    change:  { '@select': 'handleField', '@vendor-choice': 'handleVendorChoice' },
    keydown: { '@find-input': 'handleFindKey' },
    click:   { '@find-catalog': 'handleFindCatalog', '@find-fill': 'handleFindFill', '@save': 'handleSave', '@reset': 'handleReset' },
  };

  private lookupTimer: ReturnType<typeof setTimeout> | null = null;
  private readingTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly stopListening: () => void;   // catalog-pick 채널 닫기 — onUnmount
  private readonly stopListeningNone: () => void;   // catalog-pick-none 채널 닫기 — onUnmount

  constructor() {
    super();
    this.data = emptyProductForm();
    void this.loadVendors();
    this.stopListening = listenCatalogPick((pick: CatalogPick): void => this.applyPick(pick));
    this.stopListeningNone = listenCatalogPickNone((none: CatalogPickNone): void => this.applyPickNone(none));
  }

  override onUnmount(): void {
    this.stopListening();
    this.stopListeningNone();
    if (this.lookupTimer !== null) clearTimeout(this.lookupTimer);
    if (this.readingTimer !== null) clearTimeout(this.readingTimer);
  }

  override getTitle(): string {
    return '제품 등록 — HCLTech';
  }

  render(): HTMLElement {
    return htmlSolaWithInstance(this)`
      <div class="page page-product-create pv-page">
        <header class="page-header">
          <h1>📋 제품 등록</h1>
          <a class="btn-ghost" data-link href="/products">목록으로</a>
        </header>
        <section class="page-body pv-body">
          <div class="pc-form">${this.renderForm()}</div>
        </section>
      </div>`;
  }

  // ─── 액션 ───
  /** 카탈로그 탭이 보낸 행 — 폼에 붓고 찾기를 바로 다시 받는다(같은 제품이 이미 있나) */
  private applyPick(pick: CatalogPick): void {
    const before = this.data.spec_category;
    this.data = formWithPick(this.data, pick);
    this.rerenderForm();
    this.followCategory(before);
    void this.refreshLookup();
  }

  /** 카탈로그 탭이 보낸 원하는 결과 없음 — 약속: catalog_query · missed_query = q 로 두고 폼을 다시 그린다(카탈로그에 없습니다 — [파트넘버로] [디스크립션으로]) */
  private applyPickNone(none: CatalogPickNone): void {
    this.data = { ...this.data, catalog_query: none.q, missed_query: none.q };
    this.rerenderForm();
  }

  /** [찾기] — 찾기 칸 글을 들고 카탈로그 탭을 연다(비면 거기서 친다) */
  async handleFindCatalog(): Promise<void> {
    const q = this.data.catalog_query.trim();
    if (q === '') { openCatalogPickTab(q); return; }
    this.data = { ...this.data, finding: true, missed_query: null, error: '' };
    this.rerenderForm();
    try {
      const result = await api.searchAll(q, 0, 1);
      const bFound = result.total > 0;
      const bOpened = bFound && openCatalogPickTab(q);
      this.data = { ...this.data, finding: false, missed_query: bFound ? null : q, error: bFound && !bOpened ? POPUP_BLOCKED_WORD : '' };
    } catch (e) {
      this.data = { ...this.data, finding: false, error: `카탈로그를 찾지 못했습니다 — ${messageOf(e)}` };
    }
    this.rerenderForm();
  }

  /** 못 찾은 글을 고른 칸에 — 파트넘버면 찾기, 디스크립션이면 규칙 읽기 */
  handleFindFill(_e: Event, el: HTMLElement): void {
    const field = findFillFieldOf(el.dataset.key);
    this.data = formWithFoundText(this.data, field);
    this.rerenderForm();
    if (field === PART_NUMBER_KEY) { void this.refreshLookup(); return; }
    void this.refreshReading();
  }

  /** 규칙 읽기 상자의 자리 칸 — data-key = 역할(assert) → formWithSlotFill → scheduleReading(상자를 바로 안 다시 그려 포커스) */
  handleSlotFill(_e: Event, el: HTMLElement): void {
    const role = slotRoleOf(el.dataset.key);
    this.data = formWithSlotFill(this.data, role, (el as HTMLInputElement).value);
    this.scheduleReading();
  }

  /** 찾기 칸에서 Enter — [찾기] 와 같다 */
  handleFindKey(e: Event): void {
    if (!(e instanceof KeyboardEvent) || e.key !== 'Enter') return;
    e.preventDefault();
    void this.handleFindCatalog();
  }

  /** 칸 하나 — 카테고리 · 번호 종류 · 파트넘버는 찾기를 다시(파트넘버는 debounce · 칸을 안 다시 그려 포커스를 지킨다), 나머지는 글만 */
  handleField(_e: Event, el: HTMLElement): void {
    const field = fieldOf(el.dataset.key);
    const value = (el as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement).value;
    this.data = formWithField(this.data, field, value);
    this.followField(field);
  }

  /** 제조사 셀렉트 — 후보면 그 글, "기타" 면 글 칸을 연다(폼을 다시 그린다 — 셀렉트라 포커스 걱정 없음) */
  handleVendorChoice(_e: Event, el: HTMLElement): void {
    this.data = formWithVendorChoice(this.data, (el as HTMLSelectElement).value);
    this.rerenderForm();
  }

  /** 칸마다 뒤따르는 일 — 파트넘버 찾기 · 디스크립션 읽기(둘 다 debounce · 칸을 안 다시 그려 포커스) · 규칙 고르기 읽기 · 카테고리(찾기 + 규칙 목록) */
  private followField(field: EProductFormField): void {
    switch (field) {
      case 'part_number': this.scheduleLookup(); return;
      case 'description': this.scheduleReading(); return;
      case 'description_rule_id': void this.refreshReading(); return;
      case 'spec_category': this.rerenderForm(); void this.refreshLookup(); void this.refreshRules(); return;
      case 'vendor':
      case 'catalog_query':
      case 'search_text':
      case 'notes':
        return;
      default: {
        const never: never = field;
        throw new Error(`unhandled form field: ${String(never)}`);
      }
    }
  }

  /** 담기 · 찾기가 카테고리를 바꿨으면 규칙을 처음부터(목록 다시 받기), 아니면 읽기만 다시 — 연결이 바뀌면 상자가 나타나거나 사라진다 */
  private followCategory(before: SpecCategory | ''): void {
    if (this.data.spec_category === before) { void this.refreshReading(); return; }
    this.data = { ...this.data, description_rule_id: null, rules: [], reading: null };
    void this.refreshRules();
  }

  async handleSave(): Promise<void> {
    const block = saveBlockOrNull(this.data);
    if (block !== null) { this.data = { ...this.data, error: block }; this.rerenderForm(); return; }
    this.data = { ...this.data, saving: true, error: '' };
    this.rerenderForm();
    try {
      const detail = isChassisForm(this.data) ? await api.createServerProduct(serverInputOf(this.data)) : await api.createProduct(partInputOf(this.data));
      navigateTo(`/products/${detail.id}`);
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      const bConflict = message.startsWith(CONFLICT_PREFIX);
      this.data = { ...this.data, saving: false, error: bConflict ? `${message.slice(CONFLICT_PREFIX.length).trim()} — 제품 목록에서 그 제품번호를 찾아 보세요` : `저장하지 못했습니다 — ${message}` };
      this.rerenderForm();
    }
  }

  /** 제조사 후보 — 페이지 열 때 한 번(GET /products/vendors). 실패해도 칸은 손으로 친다 — 후보만 빈다 */
  private async loadVendors(): Promise<void> {
    try {
      const vendors = await api.fetchProductVendors();
      this.data = { ...this.data, vendors };
      this.rerenderForm();
    } catch (e) {
      console.error('제조사 후보를 못 받았다', e);
    }
  }

  handleReset(): void {
    this.data = { ...emptyProductForm(), vendors: this.data.vendors };
    this.rerenderForm();
  }

  // ─── 찾기 ───
  private scheduleLookup(): void {
    if (this.lookupTimer !== null) clearTimeout(this.lookupTimer);
    this.lookupTimer = setTimeout(() => { this.lookupTimer = null; void this.refreshLookup(); }, LOOKUP_DEBOUNCE_MS);
  }

  /**
   * 파트넘버가 있을 때 — 파트 찾기(카탈로그 17표 · 섀시 포함)는 늘, 섀시 폼이면 서버 찾기(같은 SKU 제품)를 더. 카테고리는 안 본다 — 카탈로그가 카테고리를 정한다(formWithLookup).
   * 기다리는 사이 열쇠가 바뀌면 그 답은 버린다
   */
  private async refreshLookup(): Promise<void> {
    const snapshot = this.data;
    const pn = snapshot.part_number.trim();
    if (pn === '') { this.data = { ...this.data, lookup: null }; this.rerenderForm(); void this.refreshReading(); return; }
    let lookup: FormLookup;
    try {
      lookup = await lookupOf(pn, snapshot);
    } catch (e) {
      if (!isSameLookupKey(snapshot, this.data)) return;
      this.data = { ...this.data, lookup: null, error: `찾지 못했습니다 — ${e instanceof Error ? e.message : String(e)}` };
      this.rerenderForm();
      return;
    }
    if (!isSameLookupKey(snapshot, this.data)) return;
    this.data = formWithLookup(this.data, lookup);
    this.rerenderForm();
    this.followCategory(snapshot.spec_category);
  }

  // ─── 규칙 읽기 ───
  /** 카테고리의 규칙 목록을 받아 rules 에. 기다리는 사이 카테고리가 바뀌면 그 답은 버린다. 받은 뒤 refreshReading */
  async refreshRules(): Promise<void> {
    const category = this.data.spec_category;
    if (category === '') { this.refreshReadingRegion(); return; }
    try {
      const rules = await api.fetchDescriptionRules(category);
      if (this.data.spec_category !== category) return;
      this.data = { ...this.data, rules };
    } catch (e) {
      if (this.data.spec_category !== category) return;
      this.data = { ...this.data, rules: [], error: `규칙 목록을 받지 못했습니다 — ${messageOf(e)}` };
      this.rerenderForm();
    }
    await this.refreshReading();
  }

  /** 디스크립션을 치는 동안 — READING_DEBOUNCE_MS 뒤 refreshReading */
  scheduleReading(): void {
    if (this.readingTimer !== null) clearTimeout(this.readingTimer);
    this.readingTimer = setTimeout(() => { this.readingTimer = null; void this.refreshReading(); }, READING_DEBOUNCE_MS);
  }

  /**
   * 안 보이는 때면 reading null. 보이면 readDescription({ 카테고리, 친 글, 고른 규칙 }). 열쇠(카테고리 · 글 · 규칙)가 그사이 바뀌었으면 버린다.
   * 실패하면 폼 바닥 에러 줄(드물다 — 폼째 다시). 성공이면 상자(.pc-reading)만 다시 그린다 — 디스크립션 칸 포커스를 지킨다
   */
  async refreshReading(): Promise<void> {
    const snapshot = this.data;
    if (!isReadingShown(snapshot)) { this.data = { ...this.data, reading: null }; this.refreshReadingRegion(); return; }
    let reading: DescriptionReading;   // 받는 동안 앞 결과를 그대로 둔다 — 비웠다 다시 그리면 깜박여 두 번 읽는 것처럼 보였다(2026-10-26). 처음엔 reading null 이라 "읽는 중…"
    try {
      reading = await api.readDescription({ spec_category: snapshot.spec_category, raw: snapshot.description.trim(), rule_id: snapshot.description_rule_id, fills: slotFillsOf(snapshot) });
    } catch (e) {
      if (!isSameReadingKey(snapshot, this.data)) return;
      this.data = { ...this.data, error: `디스크립션을 규칙으로 읽지 못했습니다 — ${messageOf(e)}` };
      this.rerenderForm();
      return;
    }
    if (!isSameReadingKey(snapshot, this.data)) return;
    this.data = { ...this.data, reading };
    this.refreshReadingRegion();
  }

  /** 상자 안 — 이어짐("카탈로그 설명을 씁니다") · 규칙 없음 · 필수 빔(붉은 자리) · 정상("저장될 디스크립션") 넷 중 하나 + 규칙 셀렉트 · 자리 표 */
  renderReading(form: ProductFormData): string {
    if (form.spec_category === '') return '';
    if (isCatalogLinked(form)) return '<p class="pc-note">카탈로그 설명을 씁니다 — 규칙은 카탈로그 쪽에서</p>';
    if (!isReadingShown(form)) return '';
    return `${ruleSelectOf(form)}${readingBodyOf(form)}`;
  }

  /** 상자만 다시 그린다 — 그 전에 포커스된 자리 칸(역할 · 커서)을 기억하고, 다시 그린 뒤 되돌린다(fill). 읽기가 오면 치던 칸이 새 칸으로 바뀌어서 */
  private refreshReadingRegion(): void {
    const focused = focusedSlotOrNull();
    this.refreshRegion('.pc-reading', () => this.renderReading(this.data));
    this.refreshRegion('.pc-footer', () => this.renderFooter(this.data));   // 필수 빔이 [저장] 잠금을 바꾼다 — 바닥 줄엔 글 칸이 없어 포커스가 안 끊긴다
    if (focused !== null) refocusSlot(this._rootElement ?? null, '.pc-reading', focused);
  }

  // ─── 그리기 ───
  private renderForm(): string {
    const form = this.data;
    const bChassis = isChassisForm(form);
    const bLinked = isCatalogLinked(form);   // 카탈로그에 있는 번호 — 카테고리는 카탈로그 것, 셀렉트 잠금
    const bCategoryNeeded = isCategoryNeeded(form);   // 글은 있는데 카테고리가 빔 — 셀렉트 붉게 + 한 줄. 칸을 치는 동안엔 폼을 안 다시 그려, 부은 직후 · 다시 그릴 때 보인다
    const categoryOptions = SPEC_CATEGORIES.map((item) => `<option value="${item}" ${form.spec_category === item ? 'selected' : ''}>${item}</option>`).join('');
    return `
      <section class="form-card pc-card pc-one">
        <div class="pc-form-head">
          <h2>등록</h2>
          ${kindChipOf(form)}
        </div>
        <div class="pc-find-row">
          <span class="form-label">카탈로그에서 찾기 — 파트넘버 · 디스크립션</span>
          <input type="text" data-action="find-input" data-key="catalog_query" value="${escapeHtml(form.catalog_query)}" placeholder="480GB SSD · P00924-B21 · DL360 Gen10" autocomplete="off">
          <button type="button" class="btn-ghost" data-action="find-catalog" ${form.finding ? 'disabled' : ''} title="카탈로그 전체 검색이 새 탭으로 열립니다 — 줄의 [선택] 을 누르면 여기에 담깁니다">${form.finding ? '찾는 중…' : '찾기 ↗'}</button>
        </div>
        ${missedNoteOf(form)}
        <div class="form-grid pc-fields">
          <label class="form-field ${bCategoryNeeded ? 'is-missing' : ''}"><span class="form-label">카테고리 <b class="pc-req">*</b></span>
            <select data-action="select" data-key="spec_category" ${bLinked ? 'disabled title="카탈로그의 카테고리 — 파트넘버를 바꾸면 풀립니다"' : ''}><option value="">카테고리 선택</option>${categoryOptions}</select>
            ${bCategoryNeeded ? '<span class="pc-error">카테고리를 골라 주세요 — 고르면 디스크립션을 규칙으로 읽습니다</span>' : ''}
          </label>
          <label class="form-field pc-span-2"><span class="form-label">${bChassis ? '파트넘버(SKU)' : '파트넘버'} <b class="pc-req">*</b></span>
            <input type="text" class="mono" data-action="field" data-key="part_number" value="${escapeHtml(form.part_number)}" placeholder="${bChassis ? '867959-B21' : 'P00924-B21'}" autocomplete="off">
          </label>
          ${lookupNoteOf(form)}
          ${pnCaseNoteOf(form.part_number)}
          ${bChassis ? '' : vendorFieldOf(form, bLinked)}
          <label class="form-field form-field-wide"><span class="form-label">디스크립션 <b class="pc-req">*</b></span>
            <input type="text" data-action="field" data-key="description" value="${escapeHtml(form.description)}">
          </label>
          <div class="form-field form-field-wide pc-reading">${this.renderReading(form)}</div>
          <label class="form-field form-field-wide"><span class="form-label">검색어 — 별칭 · 통칭 한 줄</span>
            <input type="text" data-action="field" data-key="search_text" value="${escapeHtml(form.search_text)}">
          </label>
          <label class="form-field form-field-wide"><span class="form-label">메모 — 우리가 파는 단위의 사정(단종 · 대체)</span>
            <textarea data-action="field" data-key="notes" rows="2">${escapeHtml(form.notes)}</textarea>
          </label>
        </div>
        <div class="pc-footer">${this.renderFooter(form)}</div>
      </section>`;
  }

  /** 바닥 줄 — 에러 · 비우기 · 저장. 규칙 읽기가 오면 이것만 다시 그린다(refreshReadingRegion) */
  private renderFooter(form: ProductFormData): string {
    const lockReason = saveLockOrNull(form);   // 글 칸은 다시 안 그리므로 "비었다" 로는 안 잠근다(누르면 에러 줄). 필수 빔은 읽기 결과로 잠근다
    return `
      <span class="pc-error">${escapeHtml(form.error)}</span>
      <span class="pv-spacer"></span>
      <button type="button" class="btn-ghost" data-action="reset" ${form.saving ? 'disabled' : ''}>비우기</button>
      <button type="button" class="btn-primary" data-action="save" ${lockReason === null ? '' : `disabled title="${escapeHtml(lockReason)}"`}>${form.saving ? '저장 중…' : '저장'}</button>`;
  }

  /** 폼을 다시 그린다 — 파트넘버를 치는 중이면 포커스 · 커서를 그 칸에 되돌린다(찾기 답이 와도 손이 안 끊기게) */
  private rerenderForm(): void {
    const active = document.activeElement;
    const bTypingPn = active instanceof HTMLInputElement && active.dataset.key === PART_NUMBER_KEY;
    this.refreshRegion('.pc-form', () => this.renderForm());
    if (!bTypingPn) return;
    const input = this._rootElement?.querySelector<HTMLInputElement>(`[data-key="${PART_NUMBER_KEY}"]`);
    if (input === null || input === undefined) return;
    input.focus();
    input.setSelectionRange(input.value.length, input.value.length);
  }
}

// ─── 규칙(순수) ───
/** [저장] 을 잠그는 이유 — 저장 중 · 규칙의 필수 자리 빔 · 찾기 결과에 같은 제품 · 열린 요청(승격이 먼저, 2026-10-30). 필수 칸이 빈 것은 안 잠근다(글 칸은 치는 동안 다시 안 그려 버튼이 낡는다 — 누르면 saveBlockOrNull 이 에러 줄로) */
function saveLockOrNull(form: ProductFormData): string | null {
  if (form.saving) return '저장 중입니다';
  const missingRoles = isReadingShown(form) ? missingRequiredRolesOf(form) : [];
  if (missingRoles.length > 0) return `필수 자리가 비었습니다 — ${missingRoles.map((role) => TOKEN_ROLE_WORD[role]).join(' · ')}`;
  const product = form.lookup === null ? null : form.lookup.product;
  if (product !== null) return `이미 등록된 제품입니다 — ${product.hclt_number}`;
  return openRequestBlockOrNull(form);
}

/** 찾기의 열쇠 둘이 같은가 — 답을 받는 사이 사람이 바꿨으면 그 답은 버린다 */
function isSameLookupKey(a: ProductFormData, b: ProductFormData): boolean {
  return a.spec_category === b.spec_category && a.part_number === b.part_number;
}

/** 규칙 읽기의 열쇠 넷이 같은가 — 답을 받는 사이 사람이 바꿨으면 그 답은 버린다. 채운 자리도 견준다(fill) */
function isSameReadingKey(a: ProductFormData, b: ProductFormData): boolean {
  return a.spec_category === b.spec_category && a.description === b.description && a.description_rule_id === b.description_rule_id && a.slot_fills === b.slot_fills;   // 채운 자리는 고칠 때마다 새 배열 — 참조로 견준다
}

/** 규칙 고르기 셀렉트 — 첫 줄(값 '') = 카테고리의 기본 규칙을 따른다, 그 규칙 이름만. 아래엔 기본 아닌 규칙 이름만
 *  (기본 규칙이 목록에 또 있으면 하나가 둘로 보였다 — 현민 2026-10-28). 규칙이 하나여도 셀렉트 그대로 */
function ruleSelectOf(form: ProductFormData): string {
  const defaultRule = form.rules.find((rule) => rule.is_default);
  const defaultWord = defaultRule === undefined ? '규칙 없음 (친 글 그대로)' : escapeHtml(defaultRule.name);
  const bDefaultChosen = form.description_rule_id === null || (defaultRule !== undefined && form.description_rule_id === defaultRule.id);
  const options = form.rules
    .filter((rule) => !rule.is_default)
    .map((rule) => `<option value="${escapeHtml(rule.id)}" ${rule.id === form.description_rule_id ? 'selected' : ''}>${escapeHtml(rule.name)}</option>`)
    .join('');
  return `
    <label class="pc-reading-rule"><span class="form-label">규칙</span>
      <select data-action="select" data-key="description_rule_id"><option value="" ${bDefaultChosen ? 'selected' : ''}>${defaultWord}</option>${options}</select>
    </label>`;
}

/** 읽은 결과 — 읽는 중 · 규칙 없음 · 자리 표 + 필수 빔(저장 잠김) / 저장될 디스크립션. 빈 자리를 막 채웠으면 다시 읽기 전까지 "다시 읽는 중…" */
function readingBodyOf(form: ProductFormData): string {
  const reading = form.reading;
  if (reading === null) return '<p class="pc-note">읽는 중…</p>';
  if (reading.rule_id === null) return '<p class="pc-note is-missing">이 카테고리엔 규칙이 없어 친 글 그대로 저장됩니다</p>';
  const missingRoles = missingRequiredRolesOf(form);
  const missingNote = missingRoles.length > 0
    ? `<p class="pc-note is-missing">필수 자리가 비었습니다 — ${missingRoles.map((role) => TOKEN_ROLE_WORD[role]).join(' · ')}. 디스크립션에 넣거나 빈 칸을 채우세요</p>`
    : '<p class="pc-note">다시 읽는 중…</p>';
  const result = reading.description === null
    ? missingNote
    : `<p class="pc-note is-found">저장될 디스크립션 — <b>${escapeHtml(reading.description)}</b></p>`;
  return `${renderSlotTable(reading, form.slot_fills)}${result}`;
}

function messageOf(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

/** 찾기 칸 아래 줄 — 못 찾은 글을 파트넘버 · 디스크립션 중 어디에 넣나. 없으면 '' */
function missedNoteOf(form: ProductFormData): string {
  if (form.missed_query === null) return '';
  return `
    <p class="pc-note is-missing">카탈로그에 없습니다 — <b>${escapeHtml(form.missed_query)}</b> 를
      <button type="button" class="dr-mini" data-action="find-fill" data-key="part_number">파트넘버로</button>
      <button type="button" class="dr-mini" data-action="find-fill" data-key="description">디스크립션으로</button> 넣기</p>`;
}

function findFillFieldOf(key: string | undefined): EFindFillField {
  const field = E_FIND_FILL_FIELDS.find((candidate) => candidate === key);
  assert(field !== undefined, `못 찾은 글 버튼의 data-key 는 E_FIND_FILL_FIELDS 중 하나 — missedNoteOf 가 그렸다: ${String(key)}`);
  return field;
}

function fieldOf(key: string | undefined): EProductFormField {
  const field = E_PRODUCT_FORM_FIELDS.find((candidate) => candidate === key);
  assert(field !== undefined, `폼 칸의 data-key 는 E_PRODUCT_FORM_FIELDS 중 하나 — renderForm 이 그렸다: ${String(key)}`);
  return field;
}

/** 머리 칩 — 카테고리가 정하는 타입 */
function kindChipOf(form: ProductFormData): string {
  if (form.spec_category === '') return '<span class="chip chip-muted">카테고리를 고르면 파트 · 서버가 정해집니다</span>';
  if (isChassisForm(form)) return '<span class="chip chip-primary">서버로 저장됩니다</span><small class="text-muted">섀시 SKU 하나 — 구성은 재고(실물)에서</small>';
  return '<span class="chip chip-primary">파트로 저장됩니다</span><small class="text-muted">구성 없는 부품 하나</small>';
}

/** 파트넘버 칸 밑 한 줄 — 같은 제품 · 담아 온 행 · 요청 중(승격이 먼저) · 카탈로그 있음(카테고리 잠김 · 옵션 칸이 아니면 어느 칸에 맞았나) · 없음. 하나만 보인다(위에서부터 첫 것) */
function lookupNoteOf(form: ProductFormData): string {
  const product = form.lookup === null ? null : form.lookup.product;
  if (product !== null) return `<p class="pc-note is-exists">이미 등록된 제품입니다 — <a class="mono" data-link href="/products/${escapeHtml(product.id)}">${escapeHtml(product.hclt_number)}</a>. 다시 만들지 않습니다</p>`;
  if (form.picked !== null) return `<p class="pc-note is-found">카탈로그에서 담아 옴 — ${escapeHtml(api.catalogTableWord(form.picked.table))}${form.picked.vendor === null ? '' : ` · ${escapeHtml(form.picked.vendor)}`} · 카테고리는 카탈로그 것 · 저장하면 연결됩니다</p>`;
  if (form.lookup === null) return '';
  const request = form.lookup.open_request;
  if (request !== null) return `<p class="pc-note is-exists">요청 중 #${request.pending_id}(${escapeHtml(api.PENDING_STATUS_WORD[request.status])}) — 승격이 먼저입니다. <a href="/catalog-entry/requests/${request.pending_id}" target="_blank" rel="noopener">요청 열기 ↗</a></p>`;
  const catalog = form.lookup.catalog;
  if (catalog !== null) {
    const vendor = catalog.vendor === null ? '' : ` · ${escapeHtml(catalog.vendor)}`;
    const column = form.lookup.matched_column;
    const via = column === null || column === 'option_part' ? '' : ` — ${PART_NUMBER_COLUMN_SENTENCE_WORD[column]} <span class="mono">${escapeHtml(form.part_number.trim())}</span> → 옵션 <span class="mono">${escapeHtml(catalog.option_part)}</span> 로`;   // 문장 안에선 한국말(QA 2026-10-20 #23)
    return `<p class="pc-note is-found">카탈로그에 있음${vendor} · 카테고리는 카탈로그 것(${escapeHtml(form.spec_category)})${via} · 저장하면 연결됩니다</p>`;
  }
  if (isChassisForm(form)) return '<p class="pc-note is-missing">카탈로그에 없는 섀시입니다</p>';
  return '<p class="pc-note is-missing">카탈로그에 없는 번호입니다 — 종류(옵션 · 스페어 · 어셈블리 · 팩토리)는 조사가 가르고, 옵션 파트넘버를 찾습니다</p>';
}

/** 찾기 둘 — 파트 찾기는 늘(카탈로그 17표 · 섀시 포함 · 같은 번호의 파트 제품), 섀시 폼이거나 카탈로그가 섀시 행이면 서버 찾기(같은 SKU 서버 제품)를 더. 제품은 서버 것이 먼저 */
async function lookupOf(pn: string, form: ProductFormData): Promise<FormLookup> {
  const part = await api.fetchProductLookup(pn);
  const bChassisRow = part.catalog !== null && toSpecCategory(part.catalog.spec_category) === CHASSIS_CATEGORY;   // 카탈로그가 섀시라 하면 폼 카테고리가 뭐든 서버 찾기도(formWithLookup 이 CHASSIS 로 잠근다)
  if (!isChassisForm(form) && !bChassisRow) return { catalog: part.catalog, matched_column: part.matched_column, product: part.product, open_request: part.open_request };
  const server = await api.fetchServerLookup(pn);
  return { catalog: part.catalog, matched_column: part.matched_column, product: server.product === null ? part.product : server.product, open_request: part.open_request };   // 섀시 SKU 의 요청도 파트 찾기가 같은 열쇠로 본다
}

/** 제조사 칸(파트 폼만 — 섀시는 기종이 준다) — 셀렉트(후보 + 기타, 2026-10-30 — datalist 는 톤이 따로 놀아 뺌). 기타면 오른쪽 칸에 글 칸(격자 한 칸씩). 카탈로그에 이어지면 잠긴다 */
function vendorFieldOf(form: ProductFormData, bLinked: boolean): string {
  const bOutside = form.vendor !== '' && !form.vendor_other && !form.vendors.includes(form.vendor);   // 카탈로그 제조사가 후보 밖이면 그 이름을 한 줄 더(고른 채 보이게)
  const choices = bOutside ? [...form.vendors, form.vendor] : form.vendors;
  const current = form.vendor_other ? VENDOR_OTHER : form.vendor;
  const options = ['', ...choices].map((choice) => `<option value="${escapeHtml(choice)}" ${choice === current ? 'selected' : ''}>${choice === '' ? '제조사 선택' : escapeHtml(choice)}</option>`).join('')
    + `<option value="${VENDOR_OTHER}" ${current === VENDOR_OTHER ? 'selected' : ''}>기타 — 직접 적기</option>`;
  const select = `<label class="form-field"><span class="form-label">제조사</span>
            <select data-action="vendor-choice" ${bLinked ? 'disabled title="카탈로그의 제조사 — 파트넘버를 바꾸면 풀립니다"' : ''}>${options}</select>
          </label>`;
  const input = !form.vendor_other ? '' : `<label class="form-field"><span class="form-label">제조사 이름 — 기타</span>
            <input type="text" data-action="field" data-key="vendor" value="${escapeHtml(form.vendor)}" placeholder="카탈로그 철자대로" autocomplete="off">
          </label>`;
  return select + input;
}

/** 친 번호가 열쇠 꼴(대문자 · 공백 하나)과 다르면 — 저장 때 그렇게 바뀐다는 한 줄(QA 2026-10-20 #28) */
function pnCaseNoteOf(partNumber: string): string {
  if (partNumber.trim() === '') return '';
  const key = pnKeyOf(partNumber);
  if (key === partNumber) return '';
  return `<p class="pc-note">파트넘버는 <span class="mono">${escapeHtml(key)}</span> 로 저장됩니다 — 대문자 · 공백 하나</p>`;
}

function navigateTo(path: string): void {
  history.pushState(null, '', path);
  window.dispatchEvent(new PopStateEvent('popstate'));
}
